export const getProjectsAllUrl = (baseUrl) =>
    `${baseUrl}/apis/projects/all?include_archived=true`;

export const isProjectArchived = (project) => Boolean(project?.is_archived);

export const isProjectCompleted = (project) =>
    String(project?.current_stage ?? '').trim().toLowerCase() === 'shipped';

export const ARCHIVE_RESTRICTED_STAGES = ['prepare to build', 'manufacturing', 'shipped'];

export const getProjectId = (project) => project?.id ?? project?.project_id;

/** True when no address fields are present. Ignores `details` notes on shipping_address. */
export const isEmptyAddress = (address) => {
    if (!address || typeof address !== 'object') return true;
    return Object.keys(address).filter((key) => key !== 'details').length === 0;
};

const hasAddressValue = (value) => String(value || '').trim() !== '';

const joinPersonName = (...parts) =>
    parts.map((part) => String(part || '').trim()).filter(Boolean).join(' ');

/**
 * Map a company record (nested shipping/billing or primary address) onto the
 * project address fields used by shipping_address / billing_address.
 */
export const projectAddressFromCompany = (company, addressType = 'shipping') => {
    if (!company || typeof company !== 'object') return null;

    const nestedKey = addressType === 'billing' ? 'billing_address' : 'shipping_address';
    const nested = company[nestedKey] && typeof company[nestedKey] === 'object'
        ? company[nestedKey]
        : {};

    const mapped = {
        full_name:
            nested.full_name
            || joinPersonName(nested.first, nested.last)
            || joinPersonName(nested.first_name, nested.last_name)
            || joinPersonName(company.primary_contact_first_name, company.primary_contact_last_name),
        email: nested.email || company.primary_contact_email || '',
        phone: nested.phone || company.primary_contact_phone || '',
        street1: nested.street1 || nested.address || nested.street_address_1 || company.street_address_1 || '',
        street2: nested.street2 || nested.street_address_2 || company.street_address_2 || '',
        city: nested.city || company.city || '',
        state: nested.state || company.state || '',
        zip: nested.zip || nested.zip_code || company.zip_code || '',
        country: nested.country || company.country || '',
    };

    const hasContent = ['full_name', 'email', 'phone', 'street1', 'street2', 'city', 'state', 'zip']
        .some((key) => hasAddressValue(mapped[key]));

    return hasContent ? mapped : null;
};

export const isProjectInArchiveRestrictedStage = (project) =>
    ARCHIVE_RESTRICTED_STAGES.includes(project?.current_stage);

export const projectHasInvoice = (project, invoices = []) => {
    const projectId = getProjectId(project);
    if (projectId == null) return false;
    return invoices.some((invoice) => invoice.project_site_id === projectId);
};

export const canUserArchiveProject = (project, { user, invoices } = {}) => {
    if (user?.is_super_admin === true) return true;
    if (isProjectInArchiveRestrictedStage(project)) return false;
    if (projectHasInvoice(project, invoices)) return false;
    return true;
};

/** Active list by default; pass includeArchived / includeCompleted to widen the list. */
export const filterProjectsForList = (projects, { includeArchived = false, includeCompleted = false } = {}) => {
    if (!projects?.length) return [];
    return projects.filter((project) => {
        if (isProjectCompleted(project)) return includeCompleted;
        if (isProjectArchived(project)) return includeArchived;
        return true;
    });
};
